import { useEffect, useMemo, useState } from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import style from "./aboutus.module.css";
import {
  FALLBACK_IR_CONFIG,
  FALLBACK_YOUTUBE_CONFIG,
  IrConfigItem,
  IrConfig,
  YoutubeConfig,
  fetchIrConfig,
  fetchYoutubeConfig,
} from "../cms/siteConfig";

type PrefetchedIrEntry = {
  objectUrl: string;
  fileName: string;
};

type PrefetchedIrMap = Record<number, PrefetchedIrEntry>;
const MIN_DOWNLOAD_SPINNER_MS = 300;

function triggerBrowserDownload(objectUrl: string, fileName: string, revokeAfterDownload: boolean): void {
  const anchor = document.createElement("a");
  anchor.href = objectUrl;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();

  if (revokeAfterDownload) {
    URL.revokeObjectURL(objectUrl);
  }
}

async function downloadByBlobFromProxy(index: number, fileName: string): Promise<void> {
  const response = await fetch(`/api/download-ir?index=${index}`);
  if (!response.ok) {
    throw new Error(`Download failed: ${response.status}`);
  }

  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);
  triggerBrowserDownload(objectUrl, fileName, true);
}

export default function AboutUsPage() {
  const [youtubeConfig, setYoutubeConfig] = useState<YoutubeConfig>(FALLBACK_YOUTUBE_CONFIG);
  const [irConfig, setIrConfig] = useState<IrConfig>(FALLBACK_IR_CONFIG);
  const [youtubeLoading, setYoutubeLoading] = useState<boolean>(true);
  const [irLoading, setIrLoading] = useState<boolean>(true);
  const [irPrefetching, setIrPrefetching] = useState<boolean>(true);
  const [prefetchedIrMap, setPrefetchedIrMap] = useState<PrefetchedIrMap>({});
  const [downloadingIndex, setDownloadingIndex] = useState<number | null>(null);

  useEffect(() => {
    let active = true;

    fetchYoutubeConfig()
      .then((nextYoutubeConfig) => {
        if (!active) return;
        setYoutubeConfig(nextYoutubeConfig);
      })
      .finally(() => {
        if (!active) return;
        setYoutubeLoading(false);
      });

    fetchIrConfig()
      .then((nextIrConfig) => {
        if (!active) return;
        setIrConfig(nextIrConfig);
      })
      .finally(() => {
        if (!active) return;
        setIrLoading(false);
      });

    return () => {
      active = false;
    };
  }, []);

  const irItems = useMemo(() => irConfig.items, [irConfig.items]);

  useEffect(() => {
    let active = true;
    const abortController = new AbortController();
    const nextPrefetchedIrMap: PrefetchedIrMap = {};

    const prefetchIrFiles = async () => {
      if (irLoading) {
        setIrPrefetching(true);
        return;
      }

      if (irItems.length === 0) {
        setPrefetchedIrMap({});
        setIrPrefetching(false);
        return;
      }

      setIrPrefetching(true);
      setPrefetchedIrMap({});

      await Promise.allSettled(
        irItems.map(async (item, index) => {
          const fileName = item.fileName?.trim() || item.name?.trim() || `${item.code || `ir-${index + 1}`}.pdf`;

          try {
            const response = await fetch(`/api/download-ir?index=${index}`, { signal: abortController.signal });
            if (!response.ok) {
              throw new Error(`Prefetch failed: ${response.status}`);
            }

            const blob = await response.blob();
            nextPrefetchedIrMap[index] = {
              objectUrl: URL.createObjectURL(blob),
              fileName,
            };
          } catch (error) {
            if (abortController.signal.aborted) {
              return;
            }
            console.warn("[cms] IR prefetch failed. download fallback will be used.", { index, error });
          }
        })
      );

      if (!active) {
        Object.values(nextPrefetchedIrMap).forEach((entry) => {
          URL.revokeObjectURL(entry.objectUrl);
        });
        return;
      }

      setPrefetchedIrMap(nextPrefetchedIrMap);
      setIrPrefetching(false);
    };

    prefetchIrFiles().catch((error) => {
      if (!active || abortController.signal.aborted) return;
      console.error("[cms] IR prefetch unexpected error:", error);
      setIrPrefetching(false);
    });

    return () => {
      active = false;
      abortController.abort();
    };
  }, [irItems, irLoading]);

  useEffect(() => {
    return () => {
      Object.values(prefetchedIrMap).forEach((entry) => {
        URL.revokeObjectURL(entry.objectUrl);
      });
    };
  }, [prefetchedIrMap]);

  const handleDownload = async (item: IrConfigItem, index: number) => {
    if (!item?.fileUrl) {
      alert("The downloadable file could not be found. Please contact the administrator.");
      return;
    }

    const fileName = item.fileName?.trim() || item.name?.trim() || `${item.code || `ir-${index + 1}`}.pdf`;
    const loadingStartedAt = Date.now();
    setDownloadingIndex(index);

    try {
      const prefetched = prefetchedIrMap[index];
      if (prefetched) {
        triggerBrowserDownload(prefetched.objectUrl, prefetched.fileName || fileName, false);
        return;
      }

      await downloadByBlobFromProxy(index, fileName);
    } catch (error) {
      console.error("[cms] IR download failed:", error);
      alert("IR file download failed. Please try again.");
    } finally {
      const elapsedMs = Date.now() - loadingStartedAt;
      const remainingMs = Math.max(0, MIN_DOWNLOAD_SPINNER_MS - elapsedMs);
      if (remainingMs > 0) {
        await new Promise((resolve) => setTimeout(resolve, remainingMs));
      }
      setDownloadingIndex((prev) => (prev === index ? null : prev));
    }
  };

  return (
    <>
      <Header />
      <section className={style["container--portfolio"]}>
        <div className={style["unit--content"]}>
          <h2 className={style["text--content"]}>About Us</h2>
          <p className={style["text--content--copy"]}>Discover THE ENGINEER's projects and vision through our videos</p>

          <div className={style["ir-actions"]}>
            {irItems.map((item, idx) => (
              <button
                key={`${item.code || "ir"}-${idx}`}
                type="button"
                className={`${style["ir-button"]} ${idx % 2 === 0 ? style["ir-button--ghost"] : style["ir-button--primary"]} ${
                  downloadingIndex === idx ? style["loading"] : ""
                }`}
                onClick={() => handleDownload(item, idx)}
                disabled={irLoading || downloadingIndex !== null}
                aria-busy={downloadingIndex === idx}
              >
                <span className={style["download-icon"]}></span>
                {item.title || item.name || item.code || "IR Download"}
              </button>
            ))}
          </div>
          {!irLoading && irItems.length === 0 && (
            <p className={style["ir-error"]}>No downloadable IR files are currently available.</p>
          )}

          <div className={style["video-grid"]} aria-busy={youtubeLoading}>
            {youtubeConfig.aboutUsVideos.map((url, idx) => (
              <div key={`${url}-${idx}`} className={style["video-card"]}>
                <iframe
                  src={url}
                  title={`About us video ${idx + 1}`}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className={style["video-iframe"]}
                />
              </div>
            ))}
          </div>
        </div>
      </section>
      <Footer />
    </>
  );
}
